import { useCallback, useEffect, useState } from 'react';
import ClientService from '../services/ClientService';
import { fetchOrbitContextSummary, OrbitClient } from '../services/OrbitChatService';

interface OrbitContextOptionsResult {
  periodOptions: string[];
  departmentOptions: string[];
  locationOptions: string[];
  classOptions: string[];
  loading: boolean;
  refresh: () => Promise<void>;
}

function uniqueValues(values: Array<string | null | undefined>, sort = true): string[] {
  const unique = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
  return sort ? unique.sort((a, b) => a.localeCompare(b)) : unique;
}

function extractCategoryName(category: Record<string, unknown>): string {
  return String(category.Name ?? category.name ?? category.label ?? '').trim();
}

function extractLocationName(location: Record<string, unknown>): string {
  return String(location.location_name ?? location.Name ?? location.name ?? location.code ?? '').trim();
}

function extractClassName(clientClass: Record<string, unknown>): string {
  return String(clientClass.Name ?? clientClass.name ?? clientClass.class_name ?? clientClass.label ?? '').trim();
}

export function useOrbitContextOptions(clientId?: number, client: OrbitClient = {}): OrbitContextOptionsResult {
  const [periodOptions, setPeriodOptions] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [classOptions, setClassOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const clientCode = client?.ClientCode ?? null;
  const clientName = client?.Name ?? null;

  const refresh = useCallback(async () => {
    if (!clientId || clientId <= 0) {
      setPeriodOptions([]);
      setDepartmentOptions([]);
      setLocationOptions([]);
      setClassOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [summary, categories, locations, classes] = await Promise.all([
        fetchOrbitContextSummary(clientId, client, { include_current_month: true }),
        ClientService.getClientCategories(clientId),
        ClientService.getClientLocations(clientId, { includeDropped: false }),
        ClientService.getClientClasses(clientId),
      ]);

      const months = Array.isArray(summary?.overall_kpis?.period_months)
        ? (summary?.overall_kpis?.period_months as string[])
        : [];

      setPeriodOptions(uniqueValues(months, false));
      setDepartmentOptions(uniqueValues((Array.isArray(categories) ? categories : []).map(extractCategoryName)));
      setLocationOptions(uniqueValues((Array.isArray(locations) ? locations : []).map(extractLocationName)));
      setClassOptions(uniqueValues((Array.isArray(classes) ? classes : []).map(extractClassName)));
    } catch {
      setPeriodOptions([]);
      setDepartmentOptions([]);
      setLocationOptions([]);
      setClassOptions([]);
    } finally {
      setLoading(false);
    }
  }, [clientCode, clientId, clientName]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { periodOptions, departmentOptions, locationOptions, classOptions, loading, refresh };
}

